import * as React from "react";

import {
  FilterProps,
  PrimaryDataTableFilter,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import {
  OrderByProps,
  PrimaryDataTableOrderBy,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { Resources, useResource } from "Translations/Resources";
import {
  StyledCell,
  StyledHeaderCell,
  StyledTable,
  StyledTablePagination,
  StyledTableWrapper,
  StyledTr,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTableStyles";

import { TablePagination } from "@material-ui/core";
import { TableSkeleton } from "Components/Shared/PrimaryDataTable/TableSkeleton";
import { isNoU } from "Utils/ObjectUtils";
import { usePrevious } from "Hooks/usePrevious";

type Props<T> = {
  isLoading: boolean;
  rows: {
    data: T;
    rowEntityID: keyof T;
  }[];
  getCell: (id: TableIDType<T>, data: T) => JSX.Element;
  columns: Column<T>[];
  getHeaderCell?: (column: Column<T>) => JSX.Element;
  onPageChange?: (page: number, rows: number) => void;
  totalCount?: number;
  onRowClick?: (data: T, id: TableIDType<T>) => void;
  resetTablePage?: boolean;
  paginationParams?: PaginationParams | null;
  hideTablePagination?: boolean;
};

export type Column<T> = {
  id: TableIDType<T>;
  label?: string;
  width?: string;
  cssClass?: string;
  filter?: FilterProps;
  orderBy?: OrderByProps<T>;
  isHidden?: boolean;
};

export type TableIDType<T> = keyof T | "action" | "detail";

export type PaginationParams = {
  page: number;
  rows: number;
};

export const PrimaryDataTable = <T extends object>(props: Props<T>) => {
  const {
    getCell,
    rows,
    getHeaderCell,
    onPageChange,
    totalCount,
    onRowClick,
    isLoading,
    paginationParams,
    hideTablePagination,
  } = props;
  const { t } = useResource();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);

  const [showLoader, setShowLoader] = React.useState(false);

  const [isOpenedFilter, setIsOpenedFilter] = React.useState(false);
  const wrapperFilterRefs = React.useRef<Array<HTMLElement | null>>([]);
  const [
    selectedFilter,
    setSelectedFilter,
  ] = React.useState<TableIDType<T> | null>(null);

  const columns = props.columns.filter(x => !x.isHidden);

  React.useEffect(() => {
    if (paginationParams) {
      setPage(paginationParams.page);
      setRowsPerPage(paginationParams.rows);
    }
    // eslint-disable-next-line
  }, [paginationParams]);

  React.useEffect(() => {
    wrapperFilterRefs.current = wrapperFilterRefs.current.slice(
      0,
      columns.length,
    );
  }, [columns]);

  React.useEffect(() => {
    const timeout = setTimeout(() => setShowLoader(isLoading), 300);
    if (!isLoading) {
      clearTimeout(timeout);
      setShowLoader(false);
    }
    return () => clearTimeout(timeout);
  }, [isLoading]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    onPageChange && onPageChange(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (newRows: number) => {
    setRowsPerPage(newRows);
    setPage(0);
    onPageChange && onPageChange(0, newRows);
  };

  const prevResetTable = usePrevious(props.resetTablePage);
  React.useEffect(() => {
    if (!isNoU(prevResetTable)) {
      handleChangeRowsPerPage(rowsPerPage);
    }
    // eslint-disable-next-line
  }, [props.resetTablePage]);

  React.useEffect(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperFilterRefs]);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const openedElement = wrapperFilterRefs.current.find(element =>
      element?.classList.contains("opened"),
    );
    if (
      !target?.classList.contains("filter-icon-wrapper") &&
      !openedElement?.contains(target)
    ) {
      setIsOpenedFilter(false);
    }
  };

  const getRows = () => {
    if (!onPageChange) {
      return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }
    return rows;
  };

  return (
    <>
      <StyledTableWrapper className={"table-wrapper"}>
        <StyledTable className={"table"}>
          {getHeaderCell && (
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <StyledHeaderCell
                    key={`${column.id as string}`}
                    className={column.cssClass}
                  >
                    <div className={"table-header-content"}>
                      <div>
                        {getHeaderCell(column)}

                        <PrimaryDataTableFilter
                          index={index}
                          column={column}
                          isOpenedFilter={isOpenedFilter}
                          setIsOpenedFilter={setIsOpenedFilter}
                          selectedFilter={selectedFilter}
                          setSelectedFilter={setSelectedFilter}
                          wrapperFilterRefs={wrapperFilterRefs}
                        />

                        <PrimaryDataTableOrderBy column={column} />
                      </div>
                    </div>
                  </StyledHeaderCell>
                ))}
              </tr>
            </thead>
          )}

          <tbody>
            {showLoader && (
              <TableSkeleton
                rowsCount={rowsPerPage}
                columnsCount={columns.length}
              />
            )}

            {!showLoader &&
              getRows().map(row => (
                <React.Fragment key={`${row.data[row.rowEntityID]}`}>
                  <StyledTr>
                    {columns.map(column => (
                      <StyledCell
                        className={column.cssClass}
                        width={column.width}
                        key={`${row.data[row.rowEntityID]}-${
                          column.id as string
                        }`}
                        isClickable={!!onRowClick && column.id !== "action"}
                        isActionCell={column.id === "action"}
                        onClick={() =>
                          onRowClick &&
                          column.id !== "action" &&
                          onRowClick(row.data, column.id)
                        }
                      >
                        <div>{getCell(column.id, row.data)}</div>
                      </StyledCell>
                    ))}
                  </StyledTr>
                </React.Fragment>
              ))}
          </tbody>
        </StyledTable>
      </StyledTableWrapper>
      {!hideTablePagination && (
        <StyledTablePagination>
          <TablePagination
            rowsPerPageOptions={[50, 100, 150, 200]}
            component="div"
            count={totalCount ?? rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={event =>
              handleChangeRowsPerPage(+event.target.value)
            }
            backIconButtonText={t(Resources.Common.TablePagination.BackButton)}
            nextIconButtonText={t(Resources.Common.TablePagination.NextButton)}
            labelRowsPerPage={t(Resources.Common.TablePagination.RowsPerPage)}
            labelDisplayedRows={paginationInfo =>
              t(Resources.Common.TablePagination.DisplayedRows, paginationInfo)
            }
          />
        </StyledTablePagination>
      )}
    </>
  );
};
